import type { users, UserRole } from '@techgitter/contracts';

const API_URL = '/api/users';

interface UsersListResponse {
  items: users[];
}

/**
 * Fetch every active user, bypassing the legacy technicians-only filter that
 * the autocomplete callers rely on. Used by the admin Users page.
 */
export async function fetchAllUsersForAdmin(signal?: AbortSignal): Promise<users[]> {
  const response = await fetch(`${API_URL}?technicians_only=false`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
  }
  const data = (await response.json()) as UsersListResponse;
  return data.items ?? [];
}

/**
 * Replace a user's roles. Backend requires the caller to hold the Admin role.
 */
export async function updateUserRoles(
  userId: string,
  roles: UserRole[],
  signal?: AbortSignal,
): Promise<{ userId: string; roles: UserRole[] }> {
  const response = await fetch(`${API_URL}/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ roles }),
    signal,
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      // ignore
    }
    throw new Error(`Failed to update roles (${response.status} ${response.statusText})${detail}`);
  }
  return response.json();
}

/**
 * Set a user's avatar colour to a locked-palette key. Backend validates the key
 * and requires the caller to hold the Admin role.
 */
export async function updateUserAvatarColor(
  userId: string,
  avatarColor: string,
  signal?: AbortSignal,
): Promise<{ userId: string; avatarColor: string }> {
  const response = await fetch(`${API_URL}/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ avatarColor }),
    signal,
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      // ignore
    }
    throw new Error(
      `Failed to update colour (${response.status} ${response.statusText})${detail}`,
    );
  }
  return response.json();
}

/**
 * Read a human-readable message out of an error response. The API answers
 * `{ error, status, timestamp }`, so the raw body is unreadable in a toast —
 * and the message matters here: a 409 names the user already holding the
 * extension.
 */
async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed?.error) return parsed.error;
    } catch {
      // Not JSON — fall through to the raw text.
    }
    if (text) return text.slice(0, 200);
  } catch {
    // ignore
  }
  return `${response.status} ${response.statusText}`;
}

/**
 * Set (or clear, by passing null) a user's 3CX extension — the extension the
 * call-record ingest matches on to attribute a call to a technician. Digits
 * only, max 10. Backend requires the Admin role and rejects an extension
 * already held by another active user with 409.
 */
export async function updateUserExtension(
  userId: string,
  extension: string | null,
  signal?: AbortSignal,
): Promise<{ userId: string; extension: string | null }> {
  const response = await fetch(`${API_URL}/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ extension }),
    signal,
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response));
  }
  return response.json();
}
